import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { displayToast } from "../../components/app/AppToast";
import SaveHeader from "../../components/app/headers/SaveHeader";
import { Select } from "../../components/ui/Select";
import Loader from "../../components/Loader";
import { SUPPORTED_LANGUAGES } from "../../config/i18n";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { reloadCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { isUserAuthentified } from "../../domains/user/utils/isUserAuthentified";
import { storePublicLang } from "../../domains/app/utils/langUtils";
import { SupportedLanguage } from "../../interfaces";
import { apiClient } from "../../services/api";
import { iosSetBackgroundColor, isIOSApp } from "../../tools/ios";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";

export default function SettingsEditLanguagePage() {
  const history = useHistory();
  const { activeLang, changeActiveLang } = useActiveLang();
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] =
    useState<SupportedLanguage>(activeLang);
  const langOptions = {
    en: "English",
    fr: "Français",
    de: "Deutsch",
    es: "Español",
    it: "Italiano",
    pt: "Português",
    ar: "اللغة العربية",
    nl: "Nederlands",
  } as const;
  const availableOptions: Partial<typeof langOptions> = Object.fromEntries(
    Object.entries(langOptions).filter(([key]) => {
      return SUPPORTED_LANGUAGES.includes(key as keyof typeof langOptions);
    })
  );

  useEffect(() => {
    initialize();
  }, []);

  async function initialize() {
    const isAuthentified = await isUserAuthentified();
    if (!isAuthentified) {
      history.replace("/discovery");
      return;
    }

    if (isIOSApp()) iosSetBackgroundColor("#FFFFFF");
    setLoading(false);
  }

  function handleChangeLang(option: { value: string }) {
    const lang = option.value;
    if (SUPPORTED_LANGUAGES.includes(lang as keyof typeof langOptions)) {
      setSelectedLanguage(lang as keyof typeof langOptions);
    }
  }

  const languageSelectOptions = Object.entries(availableOptions).map(
    ([value, label]) => ({ value, label: label ?? value })
  );

  async function handleSubmit() {
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: { language: selectedLanguage },
      });
      changeActiveLang(selectedLanguage);
      storePublicLang(selectedLanguage);
      void reloadCurrentUser();
      history.goBack();
      displayToast(t("Settings updated"), "success");
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <SaveHeader
          title={t("Edit Language")}
          saveReady={selectedLanguage !== activeLang}
          onSaveClick={handleSubmit}
        />
      }
    >
      {isLoading ? (
        <Loader expanded />
      ) : (
        <div className="p-[30px]">
          <Select
            appearance="field"
            footerAction={null}
            searchOnlyOptions={null}
            value={selectedLanguage}
            options={languageSelectOptions}
            placeholder={t("Language")}
            onChange={handleChangeLang}
            className="bg-white"
          />
        </div>
      )}
    </ScrollableLayout>
  );
}
